import clsx from 'clsx';

import type {ExtractContentBlockType} from '@/components/content-blocks/ContentBlocks';
import {Heading} from '@/components/typography/heading';
import {cn} from '@/utils/tailwind';

import type {ContainerProps} from '../../container';
import {Container} from '../../container';
import {PortableText} from '../../portable-text';

export type ContentBlockOptions =
	ExtractContentBlockType<'textSection'>['contentBlockOptions'];

type ContentBlockContainerProps = Omit<
	ContainerProps,
	'spacingBottom' | 'spacingTop'
> & {
	options?: ContentBlockOptions;
	heading?: string;
	description?: ExtractContentBlockType<'linkListSelection'>['description'];
	centered?: boolean;
	layout?: 'default' | 'column';
};

export type BlockTheme = NonNullable<ContentBlockOptions>['theme'];

export const ContentBlockContainer = ({
	children,
	options,
	className,
	heading,
	description,
	centered = false,
	layout = 'default',
	...rest
}: ContentBlockContainerProps) => {
	const theme = options?.theme;
	const hasTheme = theme === 'secondary-light' || theme === 'primary-light';

	let dataColor = 'primary-light';

	switch (theme) {
		case 'primary-light':
			dataColor = 'primary-light';
			break;
		case 'secondary-light':
			dataColor = 'neutral';
			break;
		case 'default':
			dataColor = 'primary-light';
			break;
	}

	if (layout === 'column') {
		return (
			<Container
				spacingBottom={options?.spacingBottom}
				spacingTop={options?.spacingTop}
				className={cn(
					[
						hasTheme && ['py-18', 'md:py-36'],
						theme === 'secondary-light' && [
							'bg-secondary-background-tinted',
						],
						theme === 'primary-light' && [
							'bg-primary-background-tinted',
						],
						'relative',
					],
					className,
				)}
				data-color={dataColor}
				{...rest}
			>
				<div
					className={cn(
						'grid',
						'md:grid-cols-2',
						'gap-x-22',
						'md:gap-y-18',
					)}
				>
					{heading || description?.length ? (
						<div
							className={cn([
								description?.length ? 'mb-14' : 'mb-10',
								'max-w-4xl',
								'md:order-2',
								'md:text-right',
							])}
						>
							{heading ? (
								<Heading
									level={2}
									size="h1"
									className={cn(['mb-4'])}
								>
									{heading}
								</Heading>
							) : null}

							{description?.length ? (
								<PortableText
									value={description}
									slotProps={{
										normal: {
											className: clsx(
												'text-body-lg!',
												'md:text-body-xl!',
											),
										},
									}}
									overrideClassNames={{
										ul: clsx(
											'text-body-lg!',
											'md:text-body-xl!',
										),
										ol: clsx(
											'text-body-lg!',
											'md:text-body-xl!',
										),
									}}
								/>
							) : null}
						</div>
					) : null}
					<div className={cn('md:order-1')}>{children}</div>
				</div>
			</Container>
		);
	}

	return (
		<Container
			spacingBottom={options?.spacingBottom}
			spacingTop={options?.spacingTop}
			className={cn(
				[
					hasTheme && ['py-18', 'md:py-36'],
					theme === 'secondary-light' && [
						'bg-secondary-background-tinted',
					],
					theme === 'primary-light' && [
						'bg-primary-background-tinted',
					],
					'relative',
				],
				className,
			)}
			data-color={dataColor}
			{...rest}
		>
			{heading || description?.length ? (
				<div
					className={cn([
						description?.length ? 'mb-14' : 'mb-10',
						'max-w-4xl',
						centered && ['text-center', 'mx-auto', 'text-balance'],
					])}
				>
					{heading ? (
						<Heading
							level={2}
							size="h1"
							className={cn([
								'mb-4',
								centered &&
									theme !== 'secondary-light' && [
										'text-primary-text-default',
									],
							])}
						>
							{heading}
						</Heading>
					) : null}

					{description?.length ? (
						<PortableText
							value={description}
							slotProps={{
								normal: {
									className: clsx(
										'text-body-lg!',
										'md:text-body-xl!',
									),
								},
							}}
							overrideClassNames={{
								ul: clsx('text-body-lg!', 'md:text-body-xl!'),
								ol: clsx('text-body-lg!', 'md:text-body-xl!'),
							}}
						/>
					) : null}
				</div>
			) : null}

			{children}
		</Container>
	);
};
